
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/auth/AuthContext';

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  
  // Check if we have a propertyId in the URL, which might indicate a missing property
  const isPropertyRoute = location.pathname.includes('/properties/');
  const isAddPropertyRoute = location.pathname === '/properties/add';
  const isHelpCentreRoute = location.pathname.includes('/help-centre');
  
  // If it's the properties/add route, redirect to the correct Add Property route
  React.useEffect(() => {
    if (isAddPropertyRoute) {
      // Navigate to the correct Add Property page
      navigate('/add-property', { replace: true });
    }
  }, [isAddPropertyRoute, navigate]);
  
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 text-center">
      <AlertCircle className="h-12 w-12 text-destructive mb-4" />
      <h1 className="text-6xl font-bold text-primary mb-4">404</h1>
      
      {isAddPropertyRoute ? (
        <p className="text-2xl font-semibold mb-2">Redirecting...</p>
      ) : isHelpCentreRoute ? (
        <p className="text-2xl font-semibold mb-2">Help Article Not Found</p>
      ) : isPropertyRoute ? (
        <p className="text-2xl font-semibold mb-2">Property Not Found</p>
      ) : (
        <p className="text-2xl font-semibold mb-2">Page Not Found</p>
      )}
      
      <p className="text-muted-foreground mb-8">
        {isAddPropertyRoute 
          ? "Taking you to the properties page..."
          : isHelpCentreRoute
            ? "The help article you're looking for doesn't exist or may have been moved."
            : isPropertyRoute 
              ? "The property you're looking for doesn't exist or has been removed."
              : "The page you're looking for doesn't exist or has been moved."}
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <Button 
          variant="outline" 
          onClick={() => navigate(-1)}
          className="flex items-center"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go Back
        </Button>
        
        <Button asChild>
          <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center">
            <Home className="mr-2 h-4 w-4" />
            {isAuthenticated ? "Dashboard" : "Home"}
          </Link>
        </Button>
        
        {isHelpCentreRoute && (
          <Button asChild variant="secondary">
            <Link to="/help-centre" className="flex items-center">
              <Home className="mr-2 h-4 w-4" />
              Help Centre
            </Link>
          </Button>
        )}
        
        {isPropertyRoute && (
          <Button asChild variant="secondary">
            <Link to="/properties" className="flex items-center">
              <Home className="mr-2 h-4 w-4" />
              All Properties
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
};

export default NotFound;
